import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    AlertTriangle,
    Check,
    Megaphone,
    Plus,
    RefreshCw,
    Search,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CampaignDetailsModal from "../components/campaigns/CampaignDetailsModal";
import FloatingActionsMenu from "../components/common/FloatingActionsMenu";
import PageContainer from "../components/layout/PageContainer";
import CreateProductModal from "../components/products/CreateProductModal";
import ProductCampaignTabs from "../components/products/ProductCampaignTabs";
import ProductDashboardDetails from "../components/products/ProductDashboardDetails";
import ProductDashboardList from "../components/products/ProductDashboardList";
import ProductDashboardSidePanel from "../components/products/ProductDashboardSidePanel";
import ProductFilters from "../components/products/ProductFilters";
import ProductStatsCards from "../components/products/ProductStatsCards";
import { ROUTES } from "../config/constants";
import { fetchConnections } from "../store/connectionsSlice";
import {
    checkCampaignStatusGuard,
    clearStatusGuardState,
} from "../store/outreachSlice";
import {
    clearProductDetailsState,
    clearUpdateProductState,
    fetchProductById,
    fetchProductSequenceCount,
    fetchProducts,
    setSelectedProduct,
    updateProduct,
} from "../store/productsSlice";
import { hasActiveSenderConnection } from "../utils/connectionGuard";
import useElementInView from "../hooks/useElementInView";
import useScrollSelectedIntoView from "../hooks/useScrollSelectedIntoView";

const getProductId = (product) => {
    if (typeof product === "string" || typeof product === "number") {
        return String(product).trim();
    }

    return product?.id || product?.product_id || product?.productId || "";
};

const getProductCampaignCount = (product) => {
    const count = Number(product?.dependent_sequence_count);

    return Number.isFinite(count) && count > 0 ? count : 0;
};

const getProductCreatedTimestamp = (product) => {
    const timestamp = new Date(product?.created_at || "").getTime();

    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const matchesProductSearch = (product, normalizedSearchTerm) =>
    !normalizedSearchTerm ||
    [product?.name, product?.description].some((value) =>
        String(value || "")
            .toLowerCase()
            .includes(normalizedSearchTerm),
    );

const isProductInCoverageTab = (product, coverageTab) =>
    coverageTab === "with-campaign"
        ? getProductCampaignCount(product) > 0
        : getProductCampaignCount(product) === 0;

const getProductCoverageTab = (product) =>
    getProductCampaignCount(product) > 0
        ? "with-campaign"
        : "without-campaign";

const MainProductsErrorState = ({ error, onRetry }) => (
    <div className="flex h-full min-h-[420px] w-full items-center justify-center bg-gradient-to-br from-white via-rose-50/30 to-indigo-50/30 p-6">
        <div className="w-full max-w-2xl rounded-2xl border border-rose-100 bg-white/90 p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <AlertTriangle size={26} aria-hidden="true" />
            </div>
            {error && (
                <p className="mx-auto max-w-lg px-4 py-2 text-sm font-medium text-rose-700">
                    {error}
                </p>
            )}
            <h2 className="mt-5 text-xl font-semibold text-slate-900">
                Unable to load products
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-600">
                We couldn&apos;t fetch your product data right now. Please check
                your connection and try again.
            </p>
            
            <button
                type="button"
                onClick={onRetry}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
            >
                <RefreshCw size={15} aria-hidden="true" />
                Retry
            </button>
        </div>
    </div>
);

const ProductCampaignProgressLine = ({
    hasSelectedProduct,
    hasInitiatedCampaign,
}) => {
    const steps = [
        {
            label: "1. Select Product",
            isComplete: hasSelectedProduct,
            isCurrent: !hasSelectedProduct,
        },
        {
            label: "2. Initiate Campaign",
            isComplete: hasInitiatedCampaign,
            isCurrent: hasSelectedProduct && !hasInitiatedCampaign,
        },
        {
            label: "3. Select Search",
            isComplete: false,
            isCurrent: hasInitiatedCampaign,
        },
    ];

    return (
        <section
            aria-label="Campaign setup progress"
            className="mt-4 hidden shrink-0 lg:block"
        >
            <div className="grid gap-4 text-center text-xs font-semibold lg:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[360px_minmax(0,1fr)_380px]">
                {steps.map((step, index) => (
                    <div
                        key={step.label}
                        className={`relative ${
                            index === 2 ? "lg:col-span-2 xl:col-span-1" : ""
                        }`}
                    >
                        <p
                            className={
                                step.isComplete
                                    ? "text-emerald-700"
                                    : step.isCurrent
                                      ? "text-indigo-700"
                                      : "text-slate-500"
                            }
                        >
                            {step.label}
                        </p>
                        <div className="relative mt-2 h-5">
                            <div
                                className={`absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 ${
                                    step.isComplete
                                        ? "bg-emerald-500"
                                        : step.isCurrent
                                          ? "bg-indigo-400"
                                          : "bg-slate-300"
                                }`}
                            />
                            <span
                                className={`absolute left-1/2 top-1/2 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border ${
                                    step.isComplete
                                        ? "border-emerald-500 bg-emerald-500 text-white shadow-sm shadow-emerald-100"
                                        : step.isCurrent
                                          ? "border-indigo-500 bg-white text-indigo-600"
                                          : "border-slate-300 bg-white text-slate-400"
                                }`}
                            >
                                {step.isComplete ? (
                                    <Check
                                        size={13}
                                        strokeWidth={3}
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                )}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

const ProductCampaignSectionProgressStep = ({
    label,
    isComplete,
    isCurrent,
}) => (
    <section
        aria-label={`${label} campaign setup progress`}
        className="mb-3 lg:hidden"
    >
        <p
            className={`text-center text-xs font-semibold ${
                isComplete
                    ? "text-emerald-700"
                    : isCurrent
                      ? "text-indigo-700"
                      : "text-slate-500"
            }`}
        >
            {label}
        </p>
        <div className="relative mt-2 h-5">
            <div
                className={`absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 ${
                    isComplete
                        ? "bg-emerald-500"
                        : isCurrent
                          ? "bg-indigo-500"
                          : "bg-slate-400"
                }`}
            />
            <span
                className={`absolute left-1/2 top-1/2 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border ${
                    isComplete
                        ? "border-emerald-500 bg-emerald-500 text-white shadow-sm shadow-emerald-100"
                        : isCurrent
                          ? "border-indigo-500 bg-white text-indigo-600"
                          : "border-slate-300 bg-white text-slate-400"
                }`}
            >
                {isComplete ? (
                    <Check size={13} strokeWidth={3} aria-hidden="true" />
                ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                )}
            </span>
        </div>
    </section>
);

const productPanelHeightClass =
    "lg:h-[var(--product-panel-height)] xl:h-full";
const productPanelHeightStyle = {
    "--product-panel-height": "clamp(36rem, calc(100vh - 14rem), 44rem)",
};

const Products = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const topActionsRef = useRef(null);
    const areTopActionsVisible = useElementInView(topActionsRef, {
        rootMargin: "-64px 0px 0px 0px",
        threshold: 0.9,
        minimumVisibleRatio: 0.9,
        topOffset: 64,
    });
    const consumedDashboardProductId = useRef("");
    const consumedCreateProductIntent = useRef(false);
    const organizationId = useSelector(
        (state) => state.organizationContext.data?.organization_id,
    );
    const {
        products,
        selectedProduct,
        productsStatus,
        productsError,
        productDetailsStatus,
        productDetailsError,
        updateProductStatus,
        productSequenceCountById,
        productSequenceCountStatusById,
        productSequenceCountErrorById,
    } = useSelector((state) => state.products);
    const [selectedProductId, setSelectedProductId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [productCoverageTab, setProductCoverageTab] =
        useState("with-campaign");
    const [productSortOrder, setProductSortOrder] = useState("newest");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [sidePanelMode, setSidePanelMode] = useState(null);
    const [sidePanelProduct, setSidePanelProduct] = useState(null);
    const [campaignDetailsSequenceId, setCampaignDetailsSequenceId] =
        useState(null);
    const [pendingProductSelectionId, setPendingProductSelectionId] =
        useState("");
    const [productScrollTargetId, setProductScrollTargetId] = useState("");

    const productList = useMemo(
        () => (Array.isArray(products) ? products : []),
        [products],
    );
    const productCampaignCounts = useMemo(() => {
        const withCampaignCount = productList.filter(
            (product) => getProductCampaignCount(product) > 0,
        ).length;

        return {
            withCampaignCount,
            withoutCampaignCount: productList.length - withCampaignCount,
        };
    }, [productList]);
    const effectiveProductCoverageTab = useMemo(() => {
        const normalizedSearchTerm = searchTerm.trim().toLowerCase();

        if (!normalizedSearchTerm) return productCoverageTab;

        const matchingProducts = productList.filter((product) =>
            matchesProductSearch(product, normalizedSearchTerm),
        );
        const hasMatchInActiveTab = matchingProducts.some((product) =>
            isProductInCoverageTab(product, productCoverageTab),
        );

        if (matchingProducts.length > 0 && !hasMatchInActiveTab) {
            return productCoverageTab === "with-campaign"
                ? "without-campaign"
                : "with-campaign";
        }

        return productCoverageTab;
    }, [productCoverageTab, productList, searchTerm]);
    useEffect(() => {
        if (effectiveProductCoverageTab !== productCoverageTab) {
            setProductCoverageTab(effectiveProductCoverageTab);
        }
    }, [effectiveProductCoverageTab, productCoverageTab]);
    const filteredProducts = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const filtered = productList.filter((product) => {
            const matchesCoverage = isProductInCoverageTab(
                product,
                effectiveProductCoverageTab,
            );
            const matchesSearch = matchesProductSearch(
                product,
                normalizedSearch,
            );

            return matchesCoverage && matchesSearch;
        });

        return [...filtered].sort((first, second) => {
            const difference =
                getProductCreatedTimestamp(second) -
                getProductCreatedTimestamp(first);

            return productSortOrder === "newest" ? difference : -difference;
        });
    }, [
        effectiveProductCoverageTab,
        productList,
        productSortOrder,
        searchTerm,
    ]);
    const visibleSelectedProduct =
        getProductId(selectedProduct) === selectedProductId
            ? selectedProduct
            : productList.find(
                  (product) => getProductId(product) === selectedProductId,
              ) || null;
    const shouldScrollProductIntoView =
        productsStatus === "succeeded" &&
        Boolean(productScrollTargetId) &&
        productScrollTargetId === selectedProductId;
    const handleProductScrolled = useCallback(() => {
        setProductScrollTargetId("");
    }, []);
    const selectedProductListItemRef = useScrollSelectedIntoView({
        selectedId: productScrollTargetId,
        enabled: shouldScrollProductIntoView,
        onScrolled: handleProductScrolled,
    });

    useEffect(() => {
        dispatch(fetchProducts());
    }, [dispatch]);

    useEffect(() => {
        if (
            location.state?.openCreateProduct !== true ||
            consumedCreateProductIntent.current ||
            !organizationId
        ) {
            return;
        }

        consumedCreateProductIntent.current = true;
        setIsCreateModalOpen(true);

        const { openCreateProduct, ...remainingState } = location.state;

        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: Object.keys(remainingState).length
                ? remainingState
                : null,
        });
    }, [
        location.pathname,
        location.search,
        location.state,
        navigate,
        organizationId,
    ]);

    useEffect(() => {
        const dashboardProduct = location.state?.selectedProduct || null;
        const dashboardProductId =
            getProductId(dashboardProduct) ||
            getProductId(location.state?.selectedProductId);

        if (!dashboardProductId) return;

        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: null,
        });

        if (consumedDashboardProductId.current === dashboardProductId) {
            return;
        }

        consumedDashboardProductId.current = dashboardProductId;
        setPendingProductSelectionId(dashboardProductId);
        setSidePanelProduct(null);
        setSidePanelMode(null);
        dispatch(clearProductDetailsState());
        if (dashboardProduct) {
            dispatch(setSelectedProduct(dashboardProduct));
        }
    }, [
        dispatch,
        location.pathname,
        location.search,
        location.state,
        navigate,
    ]);

    useEffect(() => {
        if (productsStatus !== "succeeded" || !pendingProductSelectionId) {
            return;
        }

        const productToSelect = productList.find(
            (product) => getProductId(product) === pendingProductSelectionId,
        );

        if (!productToSelect) return;

        const productId = getProductId(productToSelect);

        setPendingProductSelectionId("");
        setSearchTerm("");
        setProductCoverageTab(getProductCoverageTab(productToSelect));
        setSelectedProductId(productId);
        setProductScrollTargetId(productId);
        setSidePanelProduct(null);
        setSidePanelMode(null);
        dispatch(clearStatusGuardState());
        dispatch(clearProductDetailsState());
        dispatch(setSelectedProduct(productToSelect));
        dispatch(fetchProductById({ productId, organizationId }));
    }, [
        dispatch,
        organizationId,
        pendingProductSelectionId,
        productList,
        productsStatus,
    ]);

    useEffect(() => {
        if (productsStatus !== "succeeded") return;

        const hasIncomingDashboardSelection =
            Boolean(location.state?.selectedProduct) ||
            Boolean(getProductId(location.state?.selectedProductId));

        if (hasIncomingDashboardSelection || pendingProductSelectionId) {
            return;
        }

        if (productList.length === 0) {
            setSelectedProductId("");
            setSidePanelProduct(null);
            setSidePanelMode(null);
            return;
        }

        if (
            selectedProductId &&
            productList.some(
                (product) => getProductId(product) === selectedProductId,
            )
        ) {
            return;
        }

        const storedSelectedId = getProductId(selectedProduct);
        const initialProduct =
            productList.find(
                (product) => getProductId(product) === storedSelectedId,
            ) || productList[0];
        const initialProductId = getProductId(initialProduct);

        if (!initialProductId) return;

        setSelectedProductId(initialProductId);
        dispatch(setSelectedProduct(initialProduct));
        dispatch(
            fetchProductById({
                productId: initialProductId,
                organizationId,
            }),
        );
    }, [
        dispatch,
        location.state,
        organizationId,
        pendingProductSelectionId,
        productList,
        productsStatus,
        selectedProduct,
        selectedProductId,
    ]);

    const handleRetryProducts = () => {
        dispatch(fetchProducts());
    };

    const handleSelectProduct = (product) => {
        const productId = getProductId(product);

        if (!productId) return;

        setPendingProductSelectionId("");
        setSelectedProductId(productId);
        setProductScrollTargetId("");
        setSidePanelProduct(null);
        setSidePanelMode(null);
        dispatch(clearStatusGuardState());
        dispatch(clearProductDetailsState());
        dispatch(setSelectedProduct(product));
        dispatch(fetchProductById({ productId, organizationId }));
    };

    const handleRetryProductDetails = () => {
        if (!selectedProductId) return;

        dispatch(
            fetchProductById({
                productId: selectedProductId,
                organizationId,
            }),
        );
    };

    const handleCampaignBadgeIntent = (product) => {
        const productId = getProductId(product);
        const status = productSequenceCountStatusById[productId] || "idle";

        if (!productId || status === "loading" || status === "succeeded") {
            return;
        }

        dispatch(fetchProductSequenceCount({ productId, organizationId }));
    };

    const handleOpenCampaignPanel = (product) => {
        const productId = getProductId(product);

        if (!productId) return;

        setSelectedProductId(productId);
        setSidePanelProduct(product);
        setSidePanelMode("campaigns");
        dispatch(setSelectedProduct(product));
        dispatch(fetchProductById({ productId, organizationId }));
        handleCampaignBadgeIntent(product);
    };

    const handleSaveProduct = useCallback(
        async ({ name, description }) => {
            if (!selectedProductId) return false;

            try {
                await dispatch(
                    updateProduct({
                        productId: selectedProductId,
                        name,
                        description,
                        organizationId,
                    }),
                ).unwrap();
                toast.success("Product updated successfully.");
                return true;
            } catch (error) {
                toast.error(
                    error || "Unable to update product. Please try again.",
                );
                return false;
            }
        },
        [dispatch, organizationId, selectedProductId],
    );

    const handleClearUpdateState = useCallback(() => {
        dispatch(clearUpdateProductState());
    }, [dispatch]);

    const handleProductCreated = async (createdProduct) => {
        const productId = getProductId(createdProduct);

        if (productId) {
            setPendingProductSelectionId(productId);
            setProductCoverageTab("without-campaign");
        }

        await dispatch(fetchProducts());

        toast.success("Product created successfully.");
    };

    const verifyActiveSenderConnection = async () => {
        try {
            const latestConnections = await dispatch(
                fetchConnections(),
            ).unwrap();

            if (hasActiveSenderConnection(latestConnections)) {
                return true;
            }

            toast.error(
                "Please connect an active email sender before creating a campaign.",
            );
            navigate(ROUTES.CONNECTIONS);
            return false;
        } catch {
            toast.error(
                "Unable to verify sender connection. Please try again.",
            );
            return false;
        }
    };

    const handleCreateCampaign = async () => {
        if (!selectedProductId || !visibleSelectedProduct) return;

        const canStartCampaign = await verifyActiveSenderConnection();

        if (!canStartCampaign) return;

        dispatch(clearStatusGuardState());
        setSidePanelProduct(visibleSelectedProduct);
        setSidePanelMode("search-history");
    };

    const handleStartCampaignForSearch = async (search) => {
        const searchId = String(search?.id || "").trim();
        const productId = getProductId(sidePanelProduct) || selectedProductId;
        const queryString = search?.query_string?.trim();

        if (!searchId || !queryString || !productId) {
            return {
                ok: false,
                message: "This search cannot be used to create a campaign.",
            };
        }

        dispatch(clearStatusGuardState());

        try {
            const statusGuard = await dispatch(
                checkCampaignStatusGuard({
                    searchId,
                    productId,
                }),
            ).unwrap();

            if (statusGuard?.can_start === true) {
                navigate(ROUTES.SEARCH, {
                    state: {
                        startCampaignForHistorySearch: {
                            id: searchId,
                            queryString,
                        },
                        startCampaignAtDesignStep: true,
                    },
                });

                return { ok: true };
            }

            if (statusGuard?.can_start === false) {
                toast.error(
                    "A campaign is already running for this product and search combination.",
                );

                return { ok: false, showPanelError: false };
            }

            toast.error(
                "Unable to verify campaign availability right now.",
            );

            return { ok: false, showPanelError: false };
        } catch {
            toast.error(
                "Unable to verify campaign availability right now.",
            );

            return { ok: false, showPanelError: false };
        } finally {
            dispatch(clearStatusGuardState());
        }
    };

    const listStatus =
        productsStatus === "idle" ? "loading" : productsStatus;
    const hasProductsListError =
        productsStatus === "failed" || Boolean(productsError);
    const pageActions = [
        {
            label: "Search Lead",
            icon: Search,
            to: ROUTES.SEARCH,
        },
        {
            label: "My Campaigns",
            icon: Megaphone,
            to: ROUTES.CAMPAIGNS,
        },
        {
            label: "Create Product",
            icon: Plus,
            onClick: () => setIsCreateModalOpen(true),
            disabled: !organizationId,
            title: organizationId
                ? "Create a new product"
                : "Organization context is required",
        },
    ];
    const isCampaignSearchFlowActive =
        sidePanelMode === "search-history" && Boolean(sidePanelProduct);
    const hasProductSidePanel =
        !hasProductsListError && Boolean(sidePanelMode && sidePanelProduct);
    const productGridClass = hasProductSidePanel
        ? "lg:grid-cols-[400px_minmax(0,1fr)] xl:grid-cols-[420px_minmax(0,1fr)_380px]"
        : "lg:grid-cols-[440px_minmax(0,1fr)] xl:grid-cols-[460px_minmax(0,1fr)]";

    return (
        <div className="min-h-screen bg-[#F8F8FE]">
            <PageContainer
                as="main"
                className="flex min-h-[calc(100vh-2rem)] flex-col py-4 pb-20 sm:pb-20"
            >
                <header className="flex shrink-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                            My Products
                        </h1>
                        <p className="mt-2 max-w-2xl text-sm text-slate-600">
                            Manage products used for outreach campaigns and
                            create campaigns from saved products.
                        </p>
                    </div>

                    <div
                        ref={topActionsRef}
                        className="grid w-full grid-cols-3 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-end"
                    >
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.SEARCH)}
                            className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-2 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:w-auto sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Search size={16} aria-hidden="true" />
                            <span className="sm:hidden">Searches</span>
                            <span className="hidden sm:inline">
                                Search Lead
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.CAMPAIGNS)}
                            className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-2 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:w-auto sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Megaphone size={16} aria-hidden="true" />
                            <span className="sm:hidden">Campaigns</span>
                            <span className="hidden sm:inline">
                                My Campaigns
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsCreateModalOpen(true)}
                            disabled={!organizationId}
                            title={
                                organizationId
                                    ? "Create a new product"
                                    : "Organization context is required"
                            }
                            className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-2 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Plus size={16} aria-hidden="true" />
                            <span className="sm:hidden">Create Product</span>
                            <span className="hidden sm:inline">
                                Create Product
                            </span>
                        </button>
                    </div>
                </header>

                <div className="mt-4 shrink-0 sm:mt-5">
                    <ProductStatsCards
                        products={productList}
                        isError={hasProductsListError}
                    />
                </div>

                {isCampaignSearchFlowActive && (
                    <ProductCampaignProgressLine
                        hasSelectedProduct={Boolean(visibleSelectedProduct)}
                        hasInitiatedCampaign
                    />
                )}

                {!hasProductsListError && (
                    <div
                        className={`mt-4 grid min-w-0 grid-cols-1 gap-4 md:grid-cols-[minmax(280px,0.85fr)_minmax(0,2.35fr)] md:items-center ${productGridClass}`}
                    >
                        <ProductCampaignTabs
                            activeTab={effectiveProductCoverageTab}
                            onTabChange={setProductCoverageTab}
                            withCampaignCount={
                                productCampaignCounts.withCampaignCount
                            }
                            withoutCampaignCount={
                                productCampaignCounts.withoutCampaignCount
                            }
                        />
                        <ProductFilters
                            searchValue={searchTerm}
                            sortValue={productSortOrder}
                            onSearchChange={setSearchTerm}
                            onSortChange={setProductSortOrder}
                            autoFocus
                        />
                    </div>
                )}

                <div
                    style={productPanelHeightStyle}
                    className={`mt-4 grid min-h-0 flex-1 items-stretch gap-4 lg:flex-none xl:h-[var(--product-panel-height)] ${productGridClass}`}
                >
                    {hasProductsListError ? (
                        <section className="min-h-[420px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:col-span-full">
                            <MainProductsErrorState
                                error={productsError}
                                onRetry={handleRetryProducts}
                            />
                        </section>
                    ) : (
                        <>
                            <div
                                className={`min-h-0 ${productPanelHeightClass}`}
                            >
                                {isCampaignSearchFlowActive && (
                                    <ProductCampaignSectionProgressStep
                                        label="Select Product"
                                        isComplete={Boolean(
                                            visibleSelectedProduct,
                                        )}
                                        isCurrent={!visibleSelectedProduct}
                                    />
                                )}
                                <ProductDashboardList
                                    products={filteredProducts}
                                    status={listStatus}
                                    error={productsError}
                                    selectedProductId={selectedProductId}
                                    selectedItemRef={selectedProductListItemRef}
                                    searchTerm={searchTerm}
                                    onSearchTermChange={setSearchTerm}
                                    onRetry={handleRetryProducts}
                                    onSelect={handleSelectProduct}
                                    onCampaignBadgeClick={
                                        handleOpenCampaignPanel
                                    }
                                    onCampaignBadgeIntent={
                                        handleCampaignBadgeIntent
                                    }
                                    sequenceCountById={
                                        productSequenceCountById
                                    }
                                    sequenceCountStatusById={
                                        productSequenceCountStatusById
                                    }
                                    sequenceCountErrorById={
                                        productSequenceCountErrorById
                                    }
                                />
                            </div>
                            <div
                                className={`min-h-0 ${productPanelHeightClass}`}
                            >
                                {isCampaignSearchFlowActive && (
                                    <ProductCampaignSectionProgressStep
                                        label="Initiate Campaign"
                                        isComplete
                                        isCurrent={false}
                                    />
                                )}
                                <ProductDashboardDetails
                                    product={visibleSelectedProduct}
                                    status={productDetailsStatus}
                                    error={productDetailsError}
                                    updateStatus={updateProductStatus}
                                    onRetry={handleRetryProductDetails}
                                    onSave={handleSaveProduct}
                                    onCreateCampaign={handleCreateCampaign}
                                    onClearUpdateState={handleClearUpdateState}
                                />
                            </div>

                            {sidePanelMode && sidePanelProduct && (
                                <div
                                    className={`min-h-0 lg:col-span-2 xl:col-span-1 ${productPanelHeightClass}`}
                                >
                                    {isCampaignSearchFlowActive && (
                                        <ProductCampaignSectionProgressStep
                                            label="Select Search"
                                            isComplete={false}
                                            isCurrent
                                        />
                                    )}
                                    <ProductDashboardSidePanel
                                        mode={sidePanelMode}
                                        product={sidePanelProduct}
                                        organizationId={organizationId}
                                        onClose={() => {
                                            setSidePanelMode(null);
                                            setSidePanelProduct(null);
                                            dispatch(clearStatusGuardState());
                                        }}
                                        onViewCampaign={
                                            setCampaignDetailsSequenceId
                                        }
                                        onStartCampaignForSearch={
                                            handleStartCampaignForSearch
                                        }
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </PageContainer>
            <FloatingActionsMenu
                actions={pageActions}
                visible={!areTopActionsVisible}
            />

            <CreateProductModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                organizationId={organizationId}
                onProductCreated={handleProductCreated}
            />
            <CampaignDetailsModal
                isOpen={Boolean(campaignDetailsSequenceId)}
                onClose={() => setCampaignDetailsSequenceId(null)}
                sequenceId={campaignDetailsSequenceId}
            />
        </div>
    );
};

export default Products;
